import { useState, useEffect, useCallback } from "react";
import { Plus, Check, Calendar, Flag, Trash2, Tag, CheckCircle2, X } from "lucide-react";
import { ConfirmModal } from "../../../components/ui/confirm-modal";
import { fetchJsonCached, invalidateApiCache } from "../../../app/api/cache";

interface Todo {
  id: string;
  title: string;
  description: string;
  status: "pending" | "in_progress" | "completed";
  priority: "low" | "medium" | "high";
  dueDate: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

type TodoPriority = Todo["priority"];

const PRIORITY_CONFIG: Record<TodoPriority, { label: string; bg: string; text: string; indicator: string }> = {
  high:   { label: "High",   bg: "bg-error-container",        text: "text-on-error-container", indicator: "bg-error" },
  medium: { label: "Medium", bg: "bg-surface-container-high", text: "text-primary",             indicator: "bg-outline" },
  low:    { label: "Low",    bg: "border border-outline-variant", text: "text-secondary",       indicator: "bg-outline-variant" },
};

const TAGS_KEY = "sandok-todo-tags";

function isToday(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr), t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric",
    year: new Date(dateStr).getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

function toDateInputValue(dateStr: string | null): string {
  return dateStr ? dateStr.slice(0, 10) : "";
}

export function TodosPanel() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [todoTags, setTodoTags] = useState<Record<string, string[]>>({});

  const [newTitle, setNewTitle] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [newPriority, setNewPriority] = useState<TodoPriority>("medium");
  const [newTags, setNewTags] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editPriority, setEditPriority] = useState<TodoPriority>("medium");
  const [editTags, setEditTags] = useState("");

  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const confirm = (message: string, onConfirm: () => void) => setConfirmState({ message, onConfirm });

  useEffect(() => {
    const stored = localStorage.getItem(TAGS_KEY);
    if (stored) setTodoTags(JSON.parse(stored));
  }, []);

  function saveTags(map: Record<string, string[]>) {
    setTodoTags(map);
    localStorage.setItem(TAGS_KEY, JSON.stringify(map));
  }

  function parseTags(raw: string): string[] {
    return raw.split(/[\s,]+/).map(t => t.trim()).filter(Boolean).map(t => t.startsWith("#") ? t : `#${t}`);
  }

  const fetchTodos = useCallback(async () => {
    try {
      setTodos(await fetchJsonCached<Todo[]>("/api/todos"));
    } catch {
      // offline
    }
  }, []);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    const body: Record<string, unknown> = { title: newTitle.trim(), priority: newPriority };
    if (newDueDate) body.dueDate = newDueDate;
    const res = await fetch("/api/todos", {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      invalidateApiCache("/api/todos");
      const todo: Todo = await res.json();
      setTodos(prev => [todo, ...prev]);
      if (newTags.trim()) saveTags({ ...todoTags, [todo.id]: parseTags(newTags) });
      setNewTitle(""); setNewDueDate(""); setNewPriority("medium"); setNewTags("");
      setShowAddForm(false);
    }
  };

  const handleStatusChange = async (todo: Todo) => {
    const status = todo.status === "completed" ? "pending" : "completed";
    const res = await fetch(`/api/todos/${todo.id}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      invalidateApiCache("/api/todos");
      const updated: Todo = await res.json();
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
    }
  };

  const handleSaveEdit = async (todo: Todo) => {
    const body: Record<string, unknown> = {};
    if (editTitle.trim() && editTitle.trim() !== todo.title) body.title = editTitle.trim();
    if (editDueDate !== toDateInputValue(todo.dueDate)) body.dueDate = editDueDate || null;
    if (editPriority !== todo.priority) body.priority = editPriority;
    setEditingId(null);
    const newMap = { ...todoTags, [todo.id]: parseTags(editTags) };
    saveTags(newMap);
    if (Object.keys(body).length === 0) return;
    const res = await fetch(`/api/todos/${todo.id}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      invalidateApiCache("/api/todos");
      const updated: Todo = await res.json();
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
    }
  };

  const doDelete = async (id: string) => {
    await fetch(`/api/todos/${id}`, { method: "DELETE", credentials: "include" });
    invalidateApiCache("/api/todos");
    setTodos(prev => prev.filter(t => t.id !== id));
    const newMap = { ...todoTags };
    delete newMap[id];
    saveTags(newMap);
    if (editingId === id) setEditingId(null);
  };

  const startEdit = (todo: Todo) => {
    setEditingId(todo.id);
    setEditTitle(todo.title);
    setEditDueDate(toDateInputValue(todo.dueDate));
    setEditPriority(todo.priority);
    setEditTags((todoTags[todo.id] ?? []).join(" "));
  };

  const completedCount = todos.filter(t => t.status === "completed").length;

  return (
    <div className="p-md sm:p-margin-page w-full max-w-[1000px] mx-auto overflow-auto overflow-x-hidden h-full">
      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
          onCancel={() => setConfirmState(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-md mb-xl">
        <div>
          <h1 className="font-h1 text-primary">My Tasks</h1>
          <p className="font-body-md text-secondary mt-1">Pending action items and personal checklist.</p>
        </div>
        <div className="flex items-center gap-sm mt-4 sm:mt-0">
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary text-on-primary font-label-sm px-4 py-2 rounded flex items-center gap-2 hover:opacity-90 transition-opacity shadow-sm cursor-pointer"
          >
            <Plus size={18} /> Quick Add
          </button>
        </div>
      </div>

      <div className="bg-surface-container-lowest border border-outline-variant rounded-lg shadow-sm">
        {/* Column headers */}
        <div className="flex items-center px-md sm:px-lg py-3 border-b border-outline-variant bg-surface-container-low font-label-sm text-secondary uppercase tracking-wider">
          <div className="w-8" />
          <div className="flex-1">Task</div>
          <div className="hidden sm:block w-32">Due Date</div>
          <div className="hidden md:block w-44">Tags</div>
          <div className="w-8" />
        </div>

        <div className="divide-y divide-outline-variant">
          {/* Add form */}
          {showAddForm && (
            <div className="px-md sm:px-lg py-4 bg-surface-variant flex flex-col gap-3">
              <input
                autoFocus
                type="text"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") setShowAddForm(false); }}
                className="w-full bg-transparent outline-none font-body-md text-primary border-b border-outline-variant focus:border-primary pb-1 placeholder:text-outline"
                placeholder="Task description..."
              />
              <div className="flex gap-4 items-center flex-wrap">
                <div className="flex gap-2 items-center">
                  <Calendar size={14} className="text-secondary shrink-0" />
                  <input
                    type="date"
                    value={newDueDate}
                    onChange={e => setNewDueDate(e.target.value)}
                  className="min-w-0 bg-transparent outline-none font-mono-sm text-primary"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <Flag size={14} className="text-secondary shrink-0" />
                  <select
                    value={newPriority}
                    onChange={e => setNewPriority(e.target.value as TodoPriority)}
                    className="bg-transparent outline-none font-mono-sm text-primary"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div className="flex gap-2 items-center flex-1 min-w-[120px]">
                  <Tag size={14} className="text-secondary shrink-0" />
                  <input
                    type="text"
                    value={newTags}
                    onChange={e => setNewTags(e.target.value)}
                    className="bg-transparent outline-none font-mono-sm text-primary w-full placeholder:text-outline"
                    placeholder="#project #work"
                  />
                </div>
              </div>
              <div className="flex justify-end items-center gap-3">
                <button onClick={() => setShowAddForm(false)} className="text-secondary font-label-sm hover:text-primary transition-colors">Cancel</button>
                <button onClick={handleCreate} className="bg-primary text-on-primary font-label-sm px-4 py-1.5 rounded hover:opacity-90 transition-opacity">Save</button>
              </div>
            </div>
          )}

          {todos.length === 0 && !showAddForm ? (
              <div className="px-md sm:px-lg py-xl text-center">
              <p className="font-body-md text-outline">No tasks yet. Click "Quick Add" to create your first task.</p>
            </div>
          ) : (
            todos.map(todo => {
              const editing = editingId === todo.id;
              const completed = todo.status === "completed";
              const today = isToday(todo.dueDate);
              const pc = PRIORITY_CONFIG[todo.priority];
              const tags = todoTags[todo.id] ?? [];

              if (editing) {
                return (
                  <div key={todo.id} className="px-md sm:px-lg py-4 bg-surface-variant flex flex-col gap-3">
                    <input
                      autoFocus
                      type="text"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleSaveEdit(todo); if (e.key === "Escape") setEditingId(null); }}
                      className="w-full bg-transparent outline-none font-body-md text-primary border-b border-outline-variant focus:border-primary pb-1 placeholder:text-outline"
                      placeholder="Task Title"
                    />
                    <div className="flex gap-4 items-center flex-wrap">
                      <div className="flex gap-2 items-center">
                        <Calendar size={14} className="text-secondary shrink-0" />
                        <input
                          type="date"
                          value={editDueDate}
                          onChange={e => setEditDueDate(e.target.value)}
                          className="min-w-0 bg-transparent outline-none font-mono-sm text-primary"
                        />
                      </div>
                      <div className="flex gap-2 items-center">
                        <Flag size={14} className="text-secondary shrink-0" />
                        <select
                          value={editPriority}
                          onChange={e => setEditPriority(e.target.value as TodoPriority)}
                          className="bg-transparent outline-none font-mono-sm text-primary"
                        >
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                      <div className="flex gap-2 items-center flex-1 min-w-[120px]">
                        <Tag size={14} className="text-secondary shrink-0" />
                        <input
                          type="text"
                          value={editTags}
                          onChange={e => setEditTags(e.target.value)}
                          className="bg-transparent outline-none font-mono-sm text-primary w-full placeholder:text-outline"
                          placeholder="#project #work"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end items-center gap-3">
                      <button onClick={() => setEditingId(null)} className="text-secondary font-label-sm hover:text-primary transition-colors">Cancel</button>
                      <button onClick={() => confirm("Delete this task?", () => doDelete(todo.id))} className="text-error font-label-sm hover:opacity-80 transition-colors">Delete task</button>
                      <button onClick={() => handleSaveEdit(todo)} className="bg-primary text-on-primary font-label-sm px-4 py-1.5 rounded hover:opacity-90 transition-opacity">Save</button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={todo.id}
                  onClick={() => startEdit(todo)}
                  className={[
                    "flex items-center px-md sm:px-lg py-4 hover:bg-surface-variant transition-colors group cursor-pointer",
                    completed && "opacity-60",
                    today && !completed && "border-l-2 border-l-error",
                  ].filter(Boolean).join(" ")}
                >
                  {/* Checkbox */}
                  <button
                    onClick={e => { e.stopPropagation(); handleStatusChange(todo); }}
                    className={`w-5 h-5 shrink-0 mr-3 rounded-sm border-2 flex items-center justify-center transition-colors ${completed ? "border-primary bg-primary" : "border-outline-variant hover:border-primary"}`}
                    aria-label={completed ? "Mark as pending" : "Mark as completed"}
                  >
                    {completed && <Check size={12} className="text-white" />}
                  </button>

                  {/* Title */}
                  <div className="flex-1 min-w-0 mr-md">
                    <span className={`font-body-md truncate block ${completed ? "text-secondary line-through" : today ? "text-primary font-medium" : "text-primary"}`}>
                      {todo.title}
                    </span>
                  </div>

                  {/* Due Date */}
                  <div className={`hidden sm:flex w-32 items-center gap-1 font-mono-sm ${completed ? "text-outline" : today ? "text-error font-medium" : "text-secondary"}`}>
                    {completed ? <Check size={14} /> : today ? <Flag size={14} /> : <Calendar size={14} />}
                    {completed ? "Done" : formatDate(todo.dueDate) || "—"}
                  </div>

                  {/* Tags */}
                  <div className="hidden md:flex w-44 items-center gap-1 flex-wrap">
                    {tags.length > 0 ? tags.slice(0, 2).map(tag => (
                      <span key={tag} className="font-mono-sm text-xs px-1.5 py-0.5 rounded bg-surface-container-high text-primary">
                        {tag}
                      </span>
                    )) : (
                      <span className={`font-mono-sm text-xs px-2 py-0.5 rounded ${pc.bg} ${pc.text}`}>
                        {pc.label}
                      </span>
                    )}
                  </div>

                  {/* Delete */}
                  <div className="w-8 flex justify-end">
                    <button
                      onClick={e => { e.stopPropagation(); confirm("Delete this task?", () => doDelete(todo.id)); }}
                      className="text-outline hover:text-error opacity-0 group-hover:opacity-100 transition-all p-1"
                      aria-label="Delete task"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-md sm:px-lg py-3 border-t border-outline-variant bg-surface-container-low font-label-sm text-outline flex items-center gap-1.5">
          <CheckCircle2 size={14} />
          {completedCount}/{todos.length} completed
        </div>
      </div>
    </div>
  );
}
